import { NestClientError, connectNestConnection, createNestClient, type NestClient } from "../nest/client"
import { REPOS_NAMESPACE, TASKS_NAMESPACE, parseRepoRecord, parseTaskRecord, type RepoRecord, type TaskRecord } from "../persistence/records"
import { ACTIVITY_SUMMARY_KEY, ACTIVITY_SUMMARY_NAMESPACE, buildComputerActivitySummary, parseComputerActivitySummary } from "./activitySummary"
import type { ComputerBackend, ComputerConnection, ComputerOverview, ComputerOverviewProject, ComputerSummary, OverviewBatchResult } from "./types"

const OVERVIEW_TIMEOUT_MS = 6_000
const OVERVIEW_CONCURRENCY = 3
const RECORD_PAGE_LIMIT = 500
const RECORD_PAGE_CAP = 10

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
}

async function listRecords<T>(client: NestClient, namespace: string, prefix: string, parse: (value: unknown) => T | null): Promise<T[]> {
    const items: T[] = []
    let cursor: string | undefined
    for (let page = 0; page < RECORD_PAGE_CAP; page += 1) {
        const result = await client.requestMethod(
            "appData.list",
            { namespace, prefix, limit: RECORD_PAGE_LIMIT, ...(cursor ? { cursor } : {}) },
            { timeoutMs: OVERVIEW_TIMEOUT_MS }
        )
        for (const item of result.items) {
            const parsed = parse(item.value)
            if (parsed) items.push(parsed)
        }
        if (!result.hasMore || !result.nextCursor) break
        cursor = result.nextCursor
    }
    return items
}

/**
 * The project list is what the sidebar renders for a computer that is not
 * the active one, so every overview carries it. Nest filters the namespace
 * to a guest's granted workspaces; an owner sees them all.
 */
function overviewProjects(repos: readonly RepoRecord[]): ComputerOverviewProject[] {
    return repos.map((repo) => ({
        id: repo.id,
        name: repo.name,
        ...(repo.archived ? { archived: true } : {}),
        ...(repo.color ? { color: repo.color } : {}),
        ...(repo.glyph ? { glyph: repo.glyph } : {}),
        ...(repo.projectChars ? { projectChars: repo.projectChars } : {}),
        ...(repo.projectEmoji ? { projectEmoji: repo.projectEmoji } : {}),
    }))
}

/**
 * A shared computer hides its node-level activity summary from guests, so the
 * overview is composed from the bounded task and repo records the guest can
 * see. The owner's desktop still reads the single published summary.
 */
async function readGuestActivity(client: NestClient, liveSessionIds: ReadonlySet<string>, repos: RepoRecord[]) {
    const tasks = await listRecords<TaskRecord>(client, TASKS_NAMESPACE, "task.", parseTaskRecord)
    const runningTaskIds = new Set<string>()
    for (const task of tasks) {
        if ((task.sessions ?? []).some((session) => session.nestSessionId && liveSessionIds.has(session.nestSessionId))) runningTaskIds.add(task.id)
    }
    return parseComputerActivitySummary(buildComputerActivitySummary({ repos, tasks, runningTaskIds }))
}

function isForbidden(error: unknown): boolean {
    return error instanceof NestClientError && error.code === "forbidden"
}

async function readOverview(connection: ComputerConnection): Promise<ComputerOverview> {
    const client = createNestClient()
    try {
        await connectNestConnection(client, connection, { timeoutMs: OVERVIEW_TIMEOUT_MS })
        const [status, activityResult, liveTurns, repos] = await Promise.all([
            client.requestMethod("system.initialize", { clientName: "WildLark overview", protocolVersion: 1 }, { timeoutMs: OVERVIEW_TIMEOUT_MS }),
            client
                .requestMethod("appData.get", { namespace: ACTIVITY_SUMMARY_NAMESPACE, key: ACTIVITY_SUMMARY_KEY }, { timeoutMs: OVERVIEW_TIMEOUT_MS })
                .catch((error: unknown) => {
                    if (isForbidden(error)) return { found: false as const }
                    throw error
                }),
            client.requestMethod("session.turn.live.list", {}, { timeoutMs: OVERVIEW_TIMEOUT_MS }),
            listRecords<RepoRecord>(client, REPOS_NAMESPACE, "repo.", parseRepoRecord),
        ])
        const liveSessionIds = new Set(liveTurns.turns.map((turn) => turn.sessionId))
        const guest = !status.principal.grants.some((grant) => grant.scope.type === "node" && grant.capability === "read")
        const summary = activityResult.found
            ? parseComputerActivitySummary(activityResult.value)
            : guest
              ? await readGuestActivity(client, liveSessionIds, repos)
              : null
        const projects = overviewProjects(repos)
        if (!summary) {
            return {
                nodeId: status.nodeId,
                status: "online",
                needsAttentionCount: 0,
                openTaskCount: 0,
                runningCount: 0,
                workspaceCount: projects.filter((project) => !project.archived).length,
                projects,
                recentTasks: [],
                refreshedAt: new Date().toISOString(),
                error: "Open WildLark on this computer once to publish its activity summary.",
            }
        }

        const recentTasks = summary.recentTasks.map((task) => ({
            repoName: task.repoName,
            taskId: task.taskId,
            title: task.title,
            lastActivityAt: task.lastActivityAt,
            running: task.sessionIds.some((sessionId) => liveSessionIds.has(sessionId)),
            unread: task.unread,
            attention: task.attention,
            sleeping: task.sleeping,
            workspaceId: task.workspaceId,
        }))
        return {
            nodeId: status.nodeId,
            status: "online",
            needsAttentionCount: summary.needsAttentionCount,
            openTaskCount: summary.openTaskCount,
            runningCount: recentTasks.filter((task) => task.running).length,
            workspaceCount: summary.workspaceCount,
            projects,
            recentTasks,
            refreshedAt: new Date().toISOString(),
        }
    } finally {
        client.close()
    }
}

/**
 * An unreachable computer keeps its last-known projects and tasks so the
 * sidebar does not empty out; only the running state is cleared, because a
 * computer we cannot reach cannot be running anything for us.
 */
export function offlineOverview(nodeId: string, error: string, previous?: ComputerOverview): ComputerOverview {
    return {
        nodeId,
        status: "offline",
        needsAttentionCount: previous?.needsAttentionCount ?? 0,
        openTaskCount: previous?.openTaskCount ?? 0,
        runningCount: 0,
        workspaceCount: previous?.workspaceCount ?? 0,
        projects: previous?.projects ?? [],
        recentTasks: (previous?.recentTasks ?? []).map((task) => ({ ...task, running: false })),
        refreshedAt: new Date().toISOString(),
        error,
    }
}

export async function refreshComputerOverviews(
    backend: ComputerBackend,
    computers: ComputerSummary[],
    previous: Readonly<Record<string, ComputerOverview>> = {}
): Promise<OverviewBatchResult> {
    const result: OverviewBatchResult = {
        requested: computers.length,
        succeeded: 0,
        failed: 0,
        firstErrors: [],
        overviews: {},
    }
    let nextIndex = 0
    const worker = async () => {
        for (;;) {
            const index = nextIndex
            nextIndex += 1
            const computer = computers[index]
            if (!computer) return
            try {
                const connection = await backend.getConnection(computer.nodeId)
                result.overviews[computer.nodeId] = await readOverview(connection)
                result.succeeded += 1
            } catch (error) {
                const message = errorMessage(error)
                result.overviews[computer.nodeId] = offlineOverview(computer.nodeId, message, previous[computer.nodeId])
                result.failed += 1
                if (result.firstErrors.length < 5) result.firstErrors.push({ nodeId: computer.nodeId, message })
            }
        }
    }
    await Promise.all(Array.from({ length: Math.min(OVERVIEW_CONCURRENCY, computers.length) }, () => worker()))
    return result
}
